/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { ReactElement } from 'react';
import { Component } from 'react';

import { getFeatureConfig } from '@luna/feature-config';
import { uuid } from '@luna/utils';

interface Props {
    iban: string;
}

export class Iban extends Component<Props> {
    static formatIban(iban: string): string {
        return iban
            .replace(/[^0-9A-z]/g, '')
            .replace(/(.{4})/g, '$1 ')
            .trim()
            .toUpperCase();
    }

    static unformatIban(iban: string): string {
        return iban.replace(/ /g, '').trim().toUpperCase();
    }

    static formatHighlightedSkIban(iban: string) {
        const formattedIban = Iban.formatIban(iban);
        const quadruples: (string | ReactElement)[] = formattedIban.split(' ');
        if (quadruples.length !== 6) {
            return <span>{formattedIban}</span>;
        }

        quadruples[0] += ' ';

        if (quadruples[1] !== '0000') {
            quadruples[1] = <strong key={uuid()}>{quadruples[1]} </strong>;
        }

        quadruples[2] += ' ';

        if ((quadruples[3] as string).slice(0, 2) !== '00') {
            quadruples[3] = <strong key={uuid()}>{quadruples[3]} </strong>;
        } else {
            quadruples[3] = (
                <span key={uuid()}>
                    {(quadruples[3] as string).slice(0, 2)}
                    <strong>{(quadruples[3] as string).slice(2)} </strong>
                </span>
            );
        }

        [4, 5].forEach((i) => {
            quadruples[i] = <strong key={uuid()}>{quadruples[i]} </strong>;
        });

        return <span>{quadruples}</span>;
    }

    private renderDefaultOrSkIban(): ReactElement {
        if (this.props.iban.indexOf('SK') !== 0) {
            return this.renderDefaultIban();
        }

        return <span data-cy="iban-sk">{Iban.formatHighlightedSkIban(this.props.iban)}</span>;
    }

    private renderDefaultIban(): ReactElement {
        return <span data-cy="iban-default">{Iban.formatIban(this.props.iban)}</span>;
    }

    render() {
        if (getFeatureConfig((config) => config.base.country) === 'SK') {
            return this.renderDefaultOrSkIban();
        }
        return this.renderDefaultIban();
    }
}
